import { ReactNode } from 'react';
import { loaders } from 'entities';

import { Box, Button, Column, Modal, Row } from 'components/Blocks';
import { MailIcon } from 'components/Blocks/Icons';
import { Coin } from 'components/DecorativeIcons';

import { useAuth, useUser } from 'providers/Auth';
import { useToggleV2 } from 'hooks';

import { SendEmailForm } from './SendEmail';
import { ConfirmEmailForm } from './ConfirmForm';

import { useLang } from 'locale';
import { cn } from 'styles';
import { cascadeEval } from 'utils/conditions';

type EmailConfirmProps =
  | {
      reason?: 'INCENTIVE';
      trigger?: ReactNode;
    }
  | {
      reason: 'REDEMPTION';
      trigger: ReactNode;
    };

export type EmailConfirmReason = NonNullable<EmailConfirmProps['reason']>;

export function EmailConfirm({ reason = 'INCENTIVE', trigger }: EmailConfirmProps) {
  const { t } = useLang();
  const { updateUser } = useAuth();
  const { emailConfirmed } = useUser();

  const [isSent, sentHandles] = useToggleV2();
  const [confirmed, confirmedHandle] = useToggleV2();

  const [{ enabled, rewardPoints }] = loaders.users.emailConfirmation.useV2({
    defaultValue: { enabled: false, rewardPoints: 0 },
    disabled: !!emailConfirmed,
    refreshMinutes: 120,
    refetchOnMount: false,
  });

  if (emailConfirmed || !enabled) return null;

  const closeAfterConfirm = () => {
    updateUser({ emailConfirmed: true });
  };

  return (
    <Modal
      allowEscape={false}
      showCloseButton={!confirmed}
      trigger={
        trigger ?? (
          <button
            className={cn(
              'flex items-center gap-2 rounded-lg border bg-background px-3 py-2 transition-colors hover:border-primary hover:bg-primary/5',
              'mx-auto small_tablet:w-[calc(100%-2rem)]',
            )}
          >
            <Box className="bg-primary p-2 text-primary-foreground" border="circle">
              <MailIcon size={16} />
            </Box>

            <Column gap="1">
              <p className="font-semibold text-primary">{t('verify-email')}</p>

              <Row gap="2">
                <Coin size={18} />
                <span className="mt-1 text-sm font-medium leading-none text-warning">
                  {t('+points', { rewardPoints })}
                </span>
              </Row>
            </Column>
          </button>
        )
      }
    >
      {cascadeEval([
        {
          is: confirmed,
          then: () => (
            <Column
              alignItems="center"
              className="min-w-64 duration-300 animate-in fade-in-0 slide-in-from-bottom-11"
            >
              <Coin size={36} />

              <h3 className="text-warning">{t('+points', { rewardPoints })}!</h3>

              <p className="text-center font-semibold text-primary">{t('email-confirmed')}</p>

              <Button className="w-full" onClick={closeAfterConfirm}>
                {t('close')}
              </Button>
            </Column>
          ),
        },
        {
          is: isSent,
          then: () => (
            <ConfirmEmailForm onConfirm={confirmedHandle.turnOn} rewardPoints={rewardPoints} />
          ),
        },
        {
          is: true,
          then: () => (
            <SendEmailForm
              onSent={sentHandles.turnOn}
              rewardPoints={rewardPoints}
              reason={reason}
            />
          ),
        },
      ])}
    </Modal>
  );
}
